"use client";
// Inspired by react-hot-toast library
import * as React from "react"

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 300 // Animation duration before removal
const DEFAULT_TOAST_DURATION = 5000 // 5 seconds default

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST"
}

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString();
}

const toastTimeouts = new Map()
const autoDismissTimeouts = new Map()

const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

const clearAutoDismiss = (toastId) => {
  if (autoDismissTimeouts.has(toastId)) {
    clearTimeout(autoDismissTimeouts.get(toastId))
    autoDismissTimeouts.delete(toastId)
  }
}

const scheduleAutoDismiss = (toastId, duration) => {
  clearAutoDismiss(toastId)
  
  const timeout = setTimeout(() => {
    autoDismissTimeouts.delete(toastId)
    dispatch({ type: "DISMISS_TOAST", toastId })
  }, duration)
  
  autoDismissTimeouts.set(toastId, timeout)
}

export const reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        clearAutoDismiss(toastId)
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          clearAutoDismiss(toast.id)
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
}

const listeners = []

let memoryState = { toasts: [] }

function dispatch(action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

/**
 * Show a toast notification
 * @param {Object} props - Toast properties
 * @param {string} props.title - Toast title
 * @param {string} props.description - Toast description
 * @param {string} props.variant - Toast variant: 'default' | 'success' | 'error' | 'warning' | 'info'
 * @param {number} props.duration - Auto-dismiss duration in ms (default: 5000)
 * @param {boolean} props.showProgress - Show progress bar (default: true)
 */
function toast({
  duration = DEFAULT_TOAST_DURATION,
  showProgress = true,
  ...props
}) {
  const id = genId()
  const createdAt = Date.now()

  const update = (props) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      duration,
      showProgress,
      createdAt,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Schedule auto-dismiss
  if (duration > 0) {
    scheduleAutoDismiss(id, duration)
  }

  return {
    id: id,
    dismiss,
    update,
  }
}

// Convenience methods for different toast types
toast.success = (props) => {
  if (typeof props === 'string') {
    return toast({ title: 'Success', description: props, variant: 'success' })
  }
  return toast({ ...props, variant: 'success' })
}

toast.error = (props) => {
  if (typeof props === 'string') {
    return toast({ title: 'Error', description: props, variant: 'error' })
  }
  return toast({ ...props, variant: 'error' })
}

toast.warning = (props) => {
  if (typeof props === 'string') {
    return toast({ title: 'Warning', description: props, variant: 'warning' })
  }
  return toast({ ...props, variant: 'warning' })
}

toast.info = (props) => {
  if (typeof props === 'string') {
    return toast({ title: 'Info', description: props, variant: 'info' })
  }
  return toast({ ...props, variant: 'info' })
}

function useToast() {
  const [state, setState] = React.useState(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    };
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

export { useToast, toast }
